import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The bridge to the Mesa embed widget.
 *
 * Ported from the mesa-landing implementation, comments and all, because every
 * one of them records something that went wrong on a live call. Nothing here is
 * defensive for its own sake.
 *
 * V2, NOT V1. Both files are served, and v1 is the one that looks right in the
 * repo, but the console's own copy-paste snippet emits `dograh-widget-v2.js`.
 * That is the file that ships with the product, so it is the file that gets
 * fixed. v2 defines the whole legacy `window.DograhWidget` surface anyway, so
 * nothing is given up by using it.
 *
 * HEADLESS. v2 injects a hidden <audio> element and nothing else, and the host
 * page drives everything through `window.DograhWidget`. That is why this file
 * has no markup in it: every pixel of the panel is ours.
 *
 * MODE IS SET FROM THE SCRIPT URL. v2 reads `embedMode` and `interactionMode`
 * off its own `src` before falling back to whatever the API returns for the
 * token. Passing both here means a token misconfigured as `floating` in the
 * console cannot draw its own launcher over the page, which would put two call
 * buttons on screen and neither would be the one the visitor pressed.
 *
 * ONE WIDGET PER PAGE. `window.DograhWidget` is a singleton and v2 only defines
 * it `if (!global.DograhWidget)`, so a second script tag silently keeps the
 * first one's manager and every call would go to the wrong agent. Four agents
 * on this page therefore mean one script, torn down and reloaded when the token
 * changes.
 */

/**
 * Tokens are read with LITERAL keys on purpose.
 *
 * Create React App inlines REACT_APP_* at build time by static substitution,
 * exactly as Next does with NEXT_PUBLIC_*, so a computed lookup like
 * `process.env[agent.tokenEnv]` compiles to undefined in the browser bundle.
 * Adding an agent means adding its key here as well as to the JSON.
 */
const TOKENS = {
  REACT_APP_AGENT_TOKEN_RESTAURANT: process.env.REACT_APP_AGENT_TOKEN_RESTAURANT,
  REACT_APP_AGENT_TOKEN_TEACHING: process.env.REACT_APP_AGENT_TOKEN_TEACHING,
  REACT_APP_AGENT_TOKEN_SALON: process.env.REACT_APP_AGENT_TOKEN_SALON,
  REACT_APP_AGENT_TOKEN_NDIS: process.env.REACT_APP_AGENT_TOKEN_NDIS,
};

/** The endpoint the widget talks to. Without it nothing can connect. */
export const AGENT_API_ENDPOINT = process.env.REACT_APP_MESA_API_ENDPOINT || "";

/**
 * An agent is only offered when its token exists.
 *
 * Render nothing rather than something that fails. A button that opens a panel
 * and cannot connect is worse than no button, because it proves the opposite of
 * what it set out to prove.
 */
export function tokenFor(agent) {
  const value = agent && agent.tokenEnv ? TOKENS[agent.tokenEnv] : undefined;
  return value && value.length > 0 ? value : undefined;
}

export function isAgentLive(agent) {
  return AGENT_API_ENDPOINT.length > 0 && tokenFor(agent) !== undefined;
}

const SCRIPT_ID = "dograh-widget";

/**
 * How long to wait for `ready` before starting anyway.
 *
 * v2 calls `instance.init()` on a microtask after the script parses and reports
 * its failures to `console.error` and nowhere else, so readiness is not
 * guaranteed to arrive at all. Waiting forever would hang the panel on
 * "connecting" with nothing to read, which is the exact failure this whole
 * state machine exists to avoid. Start regardless once the wait is silly.
 */
const READY_TIMEOUT_MS = 4000;

function stamp() {
  return new Date().toLocaleTimeString("en-AU", { hour12: false });
}

let loadedToken = null;
let loader = null;

function teardownScript() {
  try {
    const existing = window.DograhWidget;
    const destroyed = existing && existing.destroy ? existing.destroy() : null;
    if (destroyed instanceof Promise) destroyed.catch(() => {});
  } catch (error) {
    // Destroying a widget that never initialised is not news.
  }
  const script = document.getElementById(SCRIPT_ID);
  if (script) script.remove();
  delete window.DograhWidget;
  delete window.DograhWidgetV2;
}

/**
 * Load the widget for one token.
 *
 * THE WIDGET CONFIGURES ITSELF FROM ITS OWN SCRIPT URL. It reads `token`,
 * `apiEndpoint`, `embedMode` and `interactionMode` off `document.currentScript`
 * and mounts immediately on parse; there is no `init({ token })` to call
 * afterwards, which is why every parameter has to be on the tag before it is
 * appended to the document.
 */
function loadWidget(agent, token) {
  if (loader && loadedToken === token) return loader;

  loadedToken = token;
  loader = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Widget cannot load on the server"));
      return;
    }

    // Tear down any previous load so the new script owns the global cleanly.
    teardownScript();

    const src = new URL("/embed/dograh-widget-v2.js", AGENT_API_ENDPOINT);
    src.searchParams.set("token", token);
    src.searchParams.set("apiEndpoint", AGENT_API_ENDPOINT);
    src.searchParams.set("embedMode", "headless");
    src.searchParams.set("interactionMode", "voice");

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = src.toString();
    // Read at mount, so it has to be set before the tag is in the document.
    script.setAttribute(
      "data-dograh-context",
      JSON.stringify({ demo: agent.id, source: "portfolio" }),
    );

    script.addEventListener(
      "load",
      () => {
        if (window.DograhWidget) resolve(window.DograhWidget);
        else reject(new Error("Widget script loaded without an API"));
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        loader = null;
        loadedToken = null;
        reject(new Error("Widget script failed to load"));
      },
      { once: true },
    );

    document.body.appendChild(script);
  });

  return loader;
}

/**
 * Resolve once the widget says it is ready, or once waiting stops being useful.
 *
 * Registered from the script's own `load` handler, which runs before the
 * microtask that calls `init()`, so the `ready` event cannot be missed in the
 * gap. The timeout covers the case where `init()` fails outright.
 */
function waitForReady(widget) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      if (widget.onReady) widget.onReady(finish);
      else finish();
    } catch (error) {
      finish();
      return;
    }
    window.setTimeout(finish, READY_TIMEOUT_MS);
  });
}

/**
 * "Live" means the visitor can hear the agent. Nothing weaker.
 *
 * The obvious signals are all lies in the failure mode that matters:
 *
 *   - `voice:connected` waits on a backend pipeline-readiness frame. Measured
 *     against the live agent it lands ~9s after media starts flowing.
 *   - The remote track firing `unmute` only means media exists.
 *   - `audio.currentTime` advances on a live MediaStream whether or not there
 *     is any sound in it. This one cost a wrong diagnosis: during an outage
 *     where the LLM never connected, `currentTime` climbed happily and the call
 *     looked healthy while nobody said a word.
 *
 * So this taps the remote stream through an AnalyserNode and measures actual
 * energy. Silence reads 0.0000; the agent speaking peaks around 0.15.
 *
 * The stall watch is one-shot and stops at first sound, because an agent that
 * has greeted you and is waiting for you to speak is silent too, and that
 * silence is correct.
 */
const AUDIBLE_RMS = 0.01;
/** Measured: connect ~9s, first word ~10s. Generous, so a slow line is not libelled. */
const STALL_AFTER_MS = 22000;

function watchForAgentAudio(onAudible, onStalled) {
  const cleanups = [];
  let done = false;

  const finish = (callback) => {
    if (done) return;
    done = true;
    for (const cleanup of cleanups) cleanup();
    callback();
  };

  const stall = window.setTimeout(() => finish(onStalled), STALL_AFTER_MS);
  cleanups.push(() => window.clearTimeout(stall));

  const bind = () => {
    const element = document.querySelector("[data-dograh-audio]");
    const stream = element ? element.srcObject : null;
    if (!stream || !stream.getAudioTracks || !stream.getAudioTracks().length) {
      return false;
    }

    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) {
      // No analyser available, so honesty is impossible. Trust the track and
      // say live rather than accuse a working call of being broken.
      finish(onAudible);
      return true;
    }

    const context = new AudioCtor();
    // Belt and braces: this runs inside a click-initiated flow, so the context
    // should already be running, but a suspended one measures pure silence and
    // would report every call as stalled.
    if (context.resume) {
      const resumed = context.resume();
      if (resumed instanceof Promise) resumed.catch(() => {});
    }
    cleanups.push(() => {
      if (!context.close) return;
      const closed = context.close();
      if (closed instanceof Promise) closed.catch(() => {});
    });

    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    const meter = window.setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
      if (Math.sqrt(sum / samples.length) > AUDIBLE_RMS) finish(onAudible);
    }, 100);
    cleanups.push(() => window.clearInterval(meter));
    return true;
  };

  const poll = window.setInterval(() => {
    if (done || bind()) window.clearInterval(poll);
  }, 250);
  cleanups.push(() => window.clearInterval(poll));

  return () => finish(() => {});
}

/**
 * `getUserMedia` is requested here rather than left to the widget.
 *
 * The widget asks for the microphone somewhere inside its own connect path, and
 * a denial surfaces as a generic error after the panel has already said
 * "connecting". Asking first means the panel can hold an honest `permission`
 * state and, on refusal, say what happened and how to undo it instead of
 * hanging on a spinner. The track is released immediately; this is a permission
 * probe, not a capture.
 */
async function requestMicrophone() {
  const media = navigator.mediaDevices;
  if (!media || !media.getUserMedia) {
    throw new Error("This browser cannot open a microphone.");
  }
  const stream = await media.getUserMedia({ audio: true });
  for (const track of stream.getTracks()) track.stop();
}

/**
 * Drive one agent's call.
 *
 * Pass the agent object while its panel is open and `null` once it closes; the
 * close path hangs up, so a call cannot outlive its own UI.
 */
export function useMesaAgent(agent) {
  const [phase, setPhase] = useState("ready");
  const [events, setEvents] = useState([]);
  const [problem, setProblem] = useState(null);
  const [seconds, setSeconds] = useState(0);

  const widgetRef = useRef(null);
  /**
   * The widget is a page-level singleton and its callbacks are not scoped to a
   * session, so a callback registered for the panel that has just closed will
   * still fire. Every handler checks this token before touching state.
   */
  const sessionRef = useRef(0);

  const log = useCallback((text) => {
    setEvents((prev) => prev.concat({ at: stamp(), text }));
  }, []);

  /** Duration ticks only while the call is up. */
  useEffect(() => {
    if (phase !== "live") return undefined;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setPhase("ready");
    setEvents([]);
    setProblem(null);
    setSeconds(0);
  }, []);

  /**
   * True once `start()` has actually handed control to the widget.
   *
   * Without this the close effect below hangs up on every panel close,
   * including a panel the visitor opened and shut without calling. `end()`
   * posts to the API to finish a session, so ending one that was never begun is
   * a guaranteed failed request.
   */
  const startedRef = useRef(false);
  /** Disposer for the audio watcher, so a closed panel stops polling. */
  const audioWatchRef = useRef(null);

  const hangUp = useCallback(() => {
    if (audioWatchRef.current) audioWatchRef.current();
    audioWatchRef.current = null;

    const widget = widgetRef.current;
    if (!widget || !startedRef.current) return;
    startedRef.current = false;

    /**
     * `end()` and `stop()` are async and they FETCH.
     *
     * A synchronous try/catch does not catch a rejected promise, so a failed
     * teardown request escaped as an unhandled rejection and surfaced as
     * "TypeError: Failed to fetch". The call is already over by the time we get
     * here and the panel is closing, so a teardown that fails changes nothing
     * the visitor can see. Swallow it on the promise, which is where it
     * actually arrives.
     */
    const quiet = (result) => {
      if (result instanceof Promise) result.catch(() => {});
    };

    try {
      if (widget.end) quiet(widget.end());
      if (widget.stop) quiet(widget.stop());
    } catch (error) {
      // Sync throws too: ending a call that has already ended is not news.
    }
  }, []);

  /**
   * `start` takes the agent rather than closing over it.
   *
   * Voice has to begin inside the click that asked for it — Safari will not
   * play remote audio otherwise, and the microphone prompt belongs to the
   * gesture. The gallery selects an agent and starts the call in the same
   * handler, so at that moment this hook is still bound to the *previous*
   * agent. Passing it explicitly is what makes the first click work; without
   * it, the first press of every button would ring the wrong line or nothing
   * at all.
   */
  const start = useCallback(async (requested) => {
    const target = requested || agent;
    if (!target) return;

    /**
     * End whatever is already up before dialling.
     *
     * Pressing a second agent's button while the first is live never unmounts
     * the panel, so the close-path hangup below never fires. Without this the
     * old session is abandoned rather than ended: `loadWidget` tears the script
     * down, but the server-side session lingers until its hour-long TTL, and
     * `startedRef` would still be describing the previous call.
     */
    hangUp();

    const token = tokenFor(target);
    if (!token || !AGENT_API_ENDPOINT) {
      setProblem("This line is not configured yet.");
      setPhase("error");
      return;
    }

    sessionRef.current += 1;
    const session = sessionRef.current;
    const alive = () => sessionRef.current === session;

    setEvents([]);
    setProblem(null);
    setSeconds(0);
    setPhase("permission");

    try {
      await requestMicrophone();
    } catch (error) {
      if (!alive()) return;
      setProblem(
        "Your browser blocked the microphone. Allow it in the address bar and press call again.",
      );
      setPhase("ready");
      return;
    }

    if (!alive()) return;
    setPhase("connecting");
    log("connecting");

    let widget;
    try {
      widget = await loadWidget(target, token);
    } catch (error) {
      if (!alive()) return;
      setProblem(
        "Could not reach the demo line. It may be blocked on this network.",
      );
      setPhase("error");
      return;
    }

    if (!alive()) return;
    widgetRef.current = widget;

    /**
     * `voice:connected` is logged, not acted on.
     *
     * It fires when the transport is up, which during one provider outage was
     * true of every single call that never made a sound. Promoting the panel on
     * it is what let a dead line show a running timer and the word "Live".
     * Audible audio is the only thing that promotes now.
     */
    if (widget.onCallConnected) {
      widget.onCallConnected(() => {
        if (!alive()) return;
        log("connected");
      });
    }
    if (widget.onStatusChange) {
      widget.onStatusChange((status) => {
        if (!alive() || typeof status !== "string") return;
        log(status);
      });
    }
    if (widget.onCallDisconnected) {
      widget.onCallDisconnected(() => {
        if (!alive()) return;
        log("disconnected");
      });
    }
    if (widget.onCallEnd) {
      widget.onCallEnd(() => {
        if (!alive()) return;
        log("ended");
        setPhase("ended");
      });
    }
    if (widget.onError) {
      widget.onError(() => {
        if (!alive()) return;
        setProblem("The call dropped. Press call again to retry.");
        setPhase("error");
      });
    }

    // Registered above, awaited here: `ready` fires from init(), so the
    // callbacks have to be on the widget before the wait, not after it.
    await waitForReady(widget);
    if (!alive()) return;

    try {
      // Marked before the call, not after: if start() rejects asynchronously
      // there is still a half-open session that has to be torn down.
      startedRef.current = true;

      // Started before start() returns, because the widget creates the audio
      // element partway through connecting and never says that it has.
      if (audioWatchRef.current) audioWatchRef.current();
      audioWatchRef.current = watchForAgentAudio(
        () => {
          if (!alive()) return;
          log("agent speaking");
          setPhase((current) =>
            current === "permission" || current === "connecting"
              ? "live"
              : current,
          );
        },
        () => {
          if (!alive()) return;
          log("no audio");
          setProblem(
            "The line connected but the agent never spoke. That is a fault my end, not yours. Press call again, or get in touch and I will show you the same thing working.",
          );
          setPhase("error");
        },
      );

      const started = widget.start ? widget.start("voice") : null;
      if (started instanceof Promise) {
        started.catch(() => {
          if (!alive()) return;
          setProblem("The call could not be started.");
          setPhase("error");
        });
      }
    } catch (error) {
      if (!alive()) return;
      setProblem("The call could not be started.");
      setPhase("error");
    }
  }, [agent, log, hangUp]);

  /**
   * Fetch the widget when the panel opens, before the visitor presses call.
   *
   * Measured cold, the script and its config fetch cost about two of the seven
   * seconds before the agent's first word. Opening the panel is a strong enough
   * signal of intent to spend that, and this way it is spent while the visitor
   * reads the card rather than while they watch a spinner. `loadWidget` caches
   * per token, so `start()` reuses this exact promise instead of repeating the
   * work.
   */
  useEffect(() => {
    if (!agent) return;
    const token = tokenFor(agent);
    if (!token || !AGENT_API_ENDPOINT) return;

    loadWidget(agent, token).catch(() => {
      // A failed prewarm is not worth reporting. `start()` retries and owns the
      // error copy; painting a failure into a panel nobody has asked to connect
      // yet would be the panel complaining about a call that was never placed.
    });
  }, [agent]);

  /** Hang up whenever the panel closes, so a call cannot outlive its own UI. */
  useEffect(() => {
    if (agent) return;
    hangUp();
    reset();
  }, [agent, hangUp, reset]);

  const end = useCallback(() => {
    hangUp();
    log("ended");
    setPhase("ended");
  }, [hangUp, log]);

  return { phase, events, problem, seconds, start, end, reset };
}
